import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PropertyService } from '../../services/property.service';
import { OrganizationService } from '../../services/organization.service';
import { DEFAULT_EXPENSE_CATEGORIES } from '../../constants/expense-categories';
import {
  ReviewDraft, ReviewLine, MONTH_NAMES, toReviewDraft, emptyReviewDraft, validateReviewDraft,
  reviewDraftToAnalysis, sumLines, describeDuplicate, apiError
} from './statement-review';

const MANUAL_ENTRY_NOTICE = 'Manual entry: add the income, expense and owner distribution lines from the statement.';

@Component({
  selector: 'app-property-statements',
  templateUrl: './property-statements.component.html',
  styleUrls: ['./property-statements.component.scss']
})
export class PropertyStatementsComponent implements OnInit, AfterViewInit {
  property: any;
  loading = false;
  allDisplayedColumns: string[] = ['property', 'date', 'view', 'analysis', 'delete'];
  propertyDisplayedColumns: string[] = ['date', 'view', 'analysis', 'delete'];
  
  analysisResults: any = null;
  selectedStatementForAnalysis: any = null;
  statementAppliedStatus: { [statementId: string]: boolean } = {};
  statementImageUrl: SafeResourceUrl | null = null;
  private statementImageBlobUrl: string | null = null;

  // Editable review step (the reviewed values are exactly what Apply uses)
  reviewDraft: ReviewDraft | null = null;
  reviewErrors: string[] = [];
  duplicateWarnings: any[] = [];
  analysisNotice: string | null = null;
  expenseCategories: string[] = [...DEFAULT_EXPENSE_CATEGORIES];
  monthNames = MONTH_NAMES;
  describeDuplicate = describeDuplicate;
  
  get displayedColumns(): string[] {
    return this.property ? this.propertyDisplayedColumns : this.allDisplayedColumns;
  }
  dataSource = new MatTableDataSource<any>();
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  // Pagination
  totalItems = 0;
  currentPage = 0;
  pageSize = 10;
  pageSizeOptions = [10, 20, 50];

  // Filters
  allProperties: any[] = [];
  selectedPropertyId: string | null = null;
  searchText: string = '';
  selectedDateFilter: string = 'all'; // 'all', 'thisMonth', 'lastMonth', 'custom'
  customStartDate: string = '';
  customEndDate: string = '';
  allStatements: any[] = [];
  filteredStatements: any[] = [];

  constructor(private propertyService: PropertyService,
              private organizationService: OrganizationService,
              private route: ActivatedRoute,
              private router: Router,
              private sanitizer: DomSanitizer) { }

  ngOnInit() {
    this.organizationService.getExpenseCategories().then(categories => {
      if (Array.isArray(categories) && categories.length > 0) {
        this.expenseCategories = categories;
      }
    }).catch(() => {
      // Keep the default list on failure
    });
    const propertyId: string | null = this.route.snapshot.queryParamMap.get('propertyId');

    if (propertyId) {
      this.loading = true;
      this.propertyService.getPublicProperty(propertyId).then(property => {
        this.property = property;
        this.loadStatements();
      }).catch(error => {
        console.error('Error loading property:', error);
        this.loading = false;
      });
    } else {
      // Load all statements for organization
      this.loadAllStatements();
    }
  }

  loadAllStatements() {
    this.loading = true;
    Promise.all([
      this.propertyService.getAllStatements(this.currentPage + 1, this.pageSize, this.selectedPropertyId || undefined),
      this.propertyService.getProperties()
    ]).then(([response, properties]: [any, any[]]) => {
      this.loading = false;
      
      // Handle paginated response
      if (response && response.data && Array.isArray(response.data)) {
        this.allStatements = response.data;
        this.totalItems = response.total || 0;
        this.currentPage = (response.page || 1) - 1; // Convert to 0-based
        this.pageSize = response.pageSize || 10;
      } else {
        // Fallback for non-paginated response
        this.allStatements = Array.isArray(response) ? response : [];
        this.totalItems = this.allStatements.length;
      }
      
      this.allProperties = Array.isArray(properties) ? properties : [];
      // Don't apply filters here - just update the data source with the paginated data
      this.filteredStatements = [...this.allStatements];
      this.checkAppliedStatus();
      this.updateDataSource();
    }).catch((error) => {
      console.error('Error loading statements:', error);
      this.loading = false;
      this.allStatements = [];
      this.allProperties = [];
      this.totalItems = 0;
    });
  }

  loadStatements() {
    if (this.property && this.property.id) {
      // Fetch statements using the API to ensure we get the latest data
      this.propertyService.getAllStatements(1, 1000, this.property.id).then((response: any) => {
        if (response && response.data && Array.isArray(response.data)) {
          this.allStatements = response.data;
        } else if (Array.isArray(response)) {
          this.allStatements = response;
        } else if (this.property.statements && Array.isArray(this.property.statements)) {
          // Fallback to property.statements if API doesn't return data
          this.allStatements = [...this.property.statements];
        } else {
          this.allStatements = [];
        }
        this.checkAppliedStatus();
        this.applyFilters();
        this.loading = false;
      }).catch(error => {
        console.error('Error loading statements:', error);
        // Fallback to property.statements if API call fails
        if (this.property.statements && Array.isArray(this.property.statements)) {
          this.allStatements = [...this.property.statements];
          this.checkAppliedStatus();
          this.applyFilters();
        } else {
          this.allStatements = [];
          this.filteredStatements = [];
          this.totalItems = 0;
          this.updateDataSource();
        }
        this.loading = false;
      });
    } else {
      this.allStatements = [];
      this.filteredStatements = [];
      this.totalItems = 0;
      this.updateDataSource();
      this.loading = false;
    }
  }

  checkAppliedStatus() {
    // Check which statements have been applied
    // Also check for saved analysis in statement objects
    this.allStatements.forEach(stmt => {
      if (stmt && stmt.id) {
        const propertyId = this.property ? this.property.id : (stmt.property ? stmt.property.id : null);
        if (propertyId) {
          // Initialize as false (not applied) until we check - this ensures Analyze button shows by default
          // Only set to true if we explicitly confirm it's applied
          this.propertyService.hasStatementBeenApplied(propertyId, stmt).then((result: any) => {
            // Only set to true if explicitly true, otherwise false
            this.statementAppliedStatus[stmt.id] = result && result.hasBeenApplied === true;
          }).catch(() => {
            // On error, default to false (not applied)
            this.statementAppliedStatus[stmt.id] = false;
          });
          
          // If statement doesn't have analysisResults but might have saved analysis, try to load it
          if (!stmt.analysisResults) {
            this.propertyService.getSavedAnalysis(propertyId, stmt).then((savedAnalysis: any) => {
              if (savedAnalysis) {
                stmt.analysisResults = savedAnalysis;
              }
            }).catch(() => {
              // No saved analysis, that's fine
            });
          }
        } else {
          // No property ID, default to not applied
          this.statementAppliedStatus[stmt.id] = false;
        }
      }
    });
  }

  isStatementApplied(stmt: any): boolean {
    // Only return true if we've explicitly checked and confirmed it's true
    // If the key doesn't exist or is false, return false (default to not applied)
    // This ensures "Analyze" button shows by default until we confirm it's applied
    if (!stmt || !stmt.id) {
      return false;
    }
    return this.statementAppliedStatus.hasOwnProperty(stmt.id) && this.statementAppliedStatus[stmt.id] === true;
  }

  addStatement(event, override: boolean = false) {
    const file: File = event && event.target ? event.target.files[0] : event;
    if (!file) {
      return;
    }
    if (event && event.target) {
      event.target.value = '';
    }
    this.loading = true;
    this.propertyService.uploadStatement(this.property.id, file, override).then((stmt: any) => {
      // Reload property to get updated statements
      this.propertyService.getPublicProperty(this.property.id).then(property => {
        this.property = property;
        this.loadStatements();
        // Auto-analyze after upload
        if (stmt) {
          this.analyzeStatement(stmt);
        }
      });
    }).catch((error) => {
      this.loading = false;
      const err = apiError(error);
      if (err.code === 'DUPLICATE_FILE') {
        const list = err.duplicates.map(d => '- ' + describeDuplicate(d)).join('\n');
        if (confirm(`This exact file was already uploaded:\n${list}\n\nImport anyway?`)) {
          this.addStatement(file, true);
        }
        return;
      }
      alert('Error uploading statement: ' + err.message);
    });
  }

  viewStatement(stmt) {
    if (!stmt || !stmt.statementUrl) {
      return;
    }
    const fileName = stmt.statementUrl.substring(stmt.statementUrl.lastIndexOf('/') + 1);
    const propertyId = this.property ? this.property.id : (stmt.property ? stmt.property.id : null);
    if (propertyId) {
      this.router.navigate(['statement-view'], { queryParams: { propertyId: propertyId, fileName: fileName } });
    }
  }

  analyzeStatement(stmt) {
    if (!stmt || !stmt.id) {
      return;
    }
    const propertyId = this.property ? this.property.id : (stmt.property ? stmt.property.id : null);
    if (!propertyId) {
      alert('Unable to determine property for this statement');
      return;
    }

    if (!stmt.statementUrl) {
      // Manually entered statement: nothing for the AI to read.
      this.selectedStatementForAnalysis = stmt;
      this.openReview(null, []);
      this.analysisNotice = MANUAL_ENTRY_NOTICE;
      return;
    }

    this.loading = true;
    this.selectedStatementForAnalysis = stmt;
    this.loadStatementImage(stmt, propertyId);
    this.propertyService.analyzeStatement(propertyId, stmt).then((results: any) => {
      this.loading = false;
      this.openReview(results || {}, results && results.duplicateStatements);
      this.analysisNotice = 'AI draft. Check every amount, the month/year and categories, then Save or Apply.';
    }).catch((error) => {
      this.loading = false;
      const err = apiError(error);
      console.error('Error analyzing statement:', err.message);
      // AI unavailable, truncated or unreadable: offer manual entry.
      if (confirm(`The statement could not be read automatically:\n${err.message}\n\nEnter the lines manually?`)) {
        this.openReview(null, []);
        this.analysisNotice = MANUAL_ENTRY_NOTICE;
      } else {
        this.closeAnalysis();
      }
    });
  }

  /**
   * "Manual entry" (no PDF): open a blank review. The statement record is only
   * created when the review is saved or applied, so cancelling leaves nothing behind.
   */
  startManualEntry() {
    if (!this.property) {
      return;
    }
    this.closeAnalysis();
    this.selectedStatementForAnalysis = { id: null, manual: true, statementUrl: null, originalFilename: 'Manual entry', property: this.property };
    this.openReview(null, []);
    this.analysisNotice = MANUAL_ENTRY_NOTICE;
  }

  /** Create the statement record for a manual entry the first time it is saved/applied. */
  private ensureStatementRecord(propertyId: string): Promise<any> {
    const stmt = this.selectedStatementForAnalysis;
    if (stmt && stmt.id) {
      return Promise.resolve(stmt);
    }
    return this.propertyService.createManualStatement(propertyId).then((created: any) => {
      this.selectedStatementForAnalysis = { ...created, property: stmt && stmt.property };
      return this.selectedStatementForAnalysis;
    });
  }

  /** Open the editable review with an AI draft or saved analysis (null = manual entry). */
  openReview(analysis: any, duplicates?: any[]) {
    this.reviewDraft = analysis ? toReviewDraft(analysis) : emptyReviewDraft();
    this.analysisResults = analysis || {};
    this.duplicateWarnings = Array.isArray(duplicates) ? duplicates : [];
    this.reviewErrors = [];
    this.analysisNotice = null;
  }

  isEditingReview(): boolean {
    return !!this.reviewDraft && !!this.selectedStatementForAnalysis && !this.isStatementApplied(this.selectedStatementForAnalysis);
  }

  addLine(section: 'income' | 'expenses' | 'owner_distributions') {
    if (!this.reviewDraft) {
      return;
    }
    const line: ReviewLine = { description: '', amount: 0 };
    if (section === 'expenses') {
      line.category = 'Other Expenses';
    }
    this.reviewDraft[section].push(line);
  }

  removeLine(section: 'income' | 'expenses' | 'owner_distributions', index: number) {
    if (this.reviewDraft) {
      this.reviewDraft[section].splice(index, 1);
    }
  }

  sectionTotal(section: 'income' | 'expenses' | 'owner_distributions'): number {
    return this.reviewDraft ? sumLines(this.reviewDraft[section]) : 0;
  }

  /** Validated analysis from the draft, or null (errors shown in the dialog). */
  private reviewedAnalysis(): any {
    if (!this.reviewDraft) {
      return null;
    }
    this.reviewErrors = validateReviewDraft(this.reviewDraft);
    if (this.reviewErrors.length) {
      return null;
    }
    return reviewDraftToAnalysis(this.reviewDraft);
  }

  private confirmDuplicatePeriod(err: any): boolean {
    const list = err.duplicates.map(d => '- ' + describeDuplicate(d)).join('\n');
    return confirm(`${err.message}\n${list}\n\nImport anyway?`);
  }

  private clearStatementImage() {
    if (this.statementImageBlobUrl) {
      URL.revokeObjectURL(this.statementImageBlobUrl);
      this.statementImageBlobUrl = null;
    }
    this.statementImageUrl = null;
  }

  loadStatementImage(stmt: any, propertyId: string) {
    // Revoke previous blob URL if exists
    this.clearStatementImage();
    
    if (stmt && stmt.statementUrl) {
      const fileName = stmt.statementUrl.substring(stmt.statementUrl.lastIndexOf('/') + 1);
      console.log('[PropertyStatements] Loading statement image:', { propertyId, fileName, statementUrl: stmt.statementUrl });
      this.propertyService.getStatementFile(propertyId, fileName).then((blob: Blob) => {
        console.log('[PropertyStatements] Statement file loaded, blob size:', blob.size, 'type:', blob.type);
        const blobUrl = URL.createObjectURL(blob);
        this.statementImageBlobUrl = blobUrl;
        this.statementImageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
        console.log('[PropertyStatements] Statement image URL created:', blobUrl);
      }).catch((error) => {
        console.error('[PropertyStatements] Error loading statement image:', error);
        this.clearStatementImage();
      });
    } else {
      console.warn('[PropertyStatements] Cannot load statement image - missing statementUrl or statement:', { stmt, hasStatementUrl: !!(stmt && stmt.statementUrl) });
    }
  }

  applyStatementForStmt(stmt) {
    // Set the selected statement and apply
    this.selectedStatementForAnalysis = stmt;
    this.applyStatement();
  }

  applyStatement(override: boolean = false) {
    if (!this.selectedStatementForAnalysis || !this.analysisResults) {
      return;
    }

    // From the review dialog, apply exactly what is on screen; from the list,
    // the server applies the saved reviewed analysis. The AI is never re-run.
    let body: any = { override };
    if (this.reviewDraft) {
      const analysis = this.reviewedAnalysis();
      if (!analysis) {
        return;
      }
      body = { analysis, override };
    }

    if (!override && !confirm('This will create expenses, invoices, and payments from the reviewed statement lines. Continue?')) {
      return;
    }

    const propertyId = this.property ? this.property.id : (this.selectedStatementForAnalysis.property ? this.selectedStatementForAnalysis.property.id : null);
    if (!propertyId) {
      alert('Unable to determine property for this statement');
      return;
    }

    let stmt = this.selectedStatementForAnalysis;
    this.loading = true;
    this.ensureStatementRecord(propertyId).then((record) => {
      stmt = record;
      return this.propertyService.applyStatement(propertyId, stmt, body);
    }).then(() => {
      this.loading = false;
      this.statementAppliedStatus[stmt.id] = true;
      this.closeAnalysis();
      alert('Statement applied successfully! Expenses, invoices, and payments have been created.');
      this.reloadAfterChange();
    }).catch((error) => {
      this.loading = false;
      const err = apiError(error);
      if (err.code === 'DUPLICATE_PERIOD') {
        if (this.confirmDuplicatePeriod(err)) {
          this.applyStatement(true);
        }
        return;
      }
      if (err.code === 'ALREADY_APPLIED') {
        this.statementAppliedStatus[stmt.id] = true;
        alert(err.message);
        this.closeAnalysis();
        this.reloadAfterChange();
        return;
      }
      console.error('Error applying statement:', err.message);
      alert('Error applying statement: ' + err.message);
    });
  }

  private reloadAfterChange() {
    if (this.property) {
      this.propertyService.getPublicProperty(this.property.id).then(property => {
        this.property = property;
        this.loadStatements();
      });
    } else {
      this.loadAllStatements();
    }
  }

  closeAnalysis() {
    this.analysisResults = null;
    this.selectedStatementForAnalysis = null;
    this.reviewDraft = null;
    this.reviewErrors = [];
    this.duplicateWarnings = [];
    this.analysisNotice = null;
    if (this.statementImageBlobUrl) {
      URL.revokeObjectURL(this.statementImageBlobUrl);
      this.statementImageBlobUrl = null;
    }
    this.statementImageUrl = null;
  }

  saveAnalysis(override: boolean = false) {
    if (!this.selectedStatementForAnalysis || !this.reviewDraft) {
      return;
    }
    const analysis = this.reviewedAnalysis();
    if (!analysis) {
      return;
    }

    const propertyId = this.property ? this.property.id : (this.selectedStatementForAnalysis.property ? this.selectedStatementForAnalysis.property.id : null);
    if (!propertyId) {
      alert('Unable to determine property for this statement');
      return;
    }

    this.loading = true;
    this.ensureStatementRecord(propertyId).then((record) =>
      this.propertyService.saveAnalysis(propertyId, record, analysis, override)
    ).then(() => {
      this.loading = false;
      alert('Review saved. You can apply it later from the list.');
      this.closeAnalysis();
      this.reloadAfterChange();
    }).catch((error) => {
      this.loading = false;
      const err = apiError(error);
      if (err.code === 'DUPLICATE_PERIOD') {
        if (this.confirmDuplicatePeriod(err)) {
          this.saveAnalysis(true);
        }
        return;
      }
      console.error('Error saving analysis:', err.message);
      alert('Error saving review: ' + err.message);
    });
  }

  viewAnalysis(stmt) {
    if (!stmt || !stmt.id) {
      return;
    }
    
    const propertyId = this.property ? this.property.id : (stmt.property ? stmt.property.id : null);
    if (!propertyId) {
      alert('Unable to determine property for this statement');
      return;
    }
    
    // Load statement image
    this.loadStatementImage(stmt, propertyId);
    
    // Check if statement has saved analysis
    if (stmt.analysisResults) {
      // Use saved analysis from statement object (editable until applied)
      this.selectedStatementForAnalysis = stmt;
      this.openReview(stmt.analysisResults, []);
    } else {
      // Load saved analysis from server
      this.loading = true;
      this.propertyService.getSavedAnalysis(propertyId, stmt).then((savedAnalysis: any) => {
        this.loading = false;
        if (savedAnalysis) {
          this.selectedStatementForAnalysis = stmt;
          this.openReview(savedAnalysis, []);
          // Update statement object with saved analysis
          stmt.analysisResults = savedAnalysis;
        } else {
          alert('No saved analysis found for this statement.');
          this.clearStatementImage();
        }
      }).catch((error) => {
        this.loading = false;
        console.error('Error loading saved analysis:', error);
        alert('Error loading saved analysis.');
        this.clearStatementImage();
      });
    }
  }

  viewAppliedAnalysis(stmt) {
    // For applied statements, load the associated items (expenses, invoices, payments)
    if (!stmt || !stmt.id) {
      return;
    }
    
    const propertyId = this.property ? this.property.id : (stmt.property ? stmt.property.id : null);
    if (!propertyId) {
      alert('Unable to determine property for this statement');
      return;
    }
    
    // Load statement image
    this.loadStatementImage(stmt, propertyId);
    
    this.loading = true;
    this.selectedStatementForAnalysis = stmt;
    this.reviewDraft = null;

    // Try to load saved analysis first (might still be there)
    this.propertyService.getSavedAnalysis(propertyId, stmt).then((savedAnalysis: any) => {
      // Load the applied items
      return this.propertyService.getStatementAppliedItems(propertyId, stmt).then((appliedItems: any) => {
        this.loading = false;
        // Combine saved analysis (if exists) with applied items
        this.analysisResults = {
          ...(savedAnalysis || {}),
          appliedItems: appliedItems,
          isApplied: true
        };
      });
    }).catch(() => {
      // If saved analysis doesn't exist, just load applied items
      this.propertyService.getStatementAppliedItems(propertyId, stmt).then((appliedItems: any) => {
        this.loading = false;
        this.analysisResults = {
          appliedItems: appliedItems,
          isApplied: true
        };
      }).catch((error) => {
        this.loading = false;
        console.error('Error loading applied items:', error);
        alert('Error loading applied items. Please try again.');
      });
    });
  }

  applyAnalysisFromList(stmt) {
    if (!stmt || !stmt.id) {
      return;
    }
    
    const propertyId = this.property ? this.property.id : (stmt.property ? stmt.property.id : null);
    if (!propertyId) {
      alert('Unable to determine property for this statement');
      return;
    }
    
    // Check if statement has saved analysis
    if (stmt.analysisResults) {
      // Use saved analysis
      this.selectedStatementForAnalysis = stmt;
      this.openReview(stmt.analysisResults, []);
      this.applyStatement();
    } else {
      // Load saved analysis from server
      this.loading = true;
      this.propertyService.getSavedAnalysis(propertyId, stmt).then((savedAnalysis: any) => {
        this.loading = false;
        if (savedAnalysis) {
          this.selectedStatementForAnalysis = stmt;
          this.openReview(savedAnalysis, []);
          this.applyStatement();
        } else {
          alert('No saved analysis found for this statement. Please analyze it first.');
        }
      }).catch((error) => {
        this.loading = false;
        console.error('Error loading saved analysis:', error);
        alert('Error loading saved analysis. Please analyze the statement first.');
      });
    }
  }

  deleteAnalysis(stmt) {
    if (!stmt || !stmt.id) {
      return;
    }
    
    if (!confirm('This will delete the saved analysis. You will need to analyze the statement again to apply it. Continue?')) {
      return;
    }
    
    const propertyId = this.property ? this.property.id : (stmt.property ? stmt.property.id : null);
    if (!propertyId) {
      alert('Unable to determine property for this statement');
      return;
    }
    
    this.loading = true;
    this.propertyService.deleteAnalysis(propertyId, stmt).then(() => {
      this.loading = false;
      alert('Analysis deleted successfully!');
      
      // Clear analysis results if this was the viewed statement
      if (this.selectedStatementForAnalysis && this.selectedStatementForAnalysis.id === stmt.id) {
        this.analysisResults = null;
        this.selectedStatementForAnalysis = null;
      }
      
      // Remove from statement object
      stmt.analysisResults = null;
      
      // Reload statements
      if (this.property) {
        this.propertyService.getPublicProperty(this.property.id).then(property => {
          this.property = property;
          this.loadStatements();
        });
      } else {
        this.loadAllStatements();
      }
    }).catch((error) => {
      this.loading = false;
      console.error('Error deleting analysis:', error);
      const errorMsg = (error && error.error && error.error.error) ? error.error.error : (error.message || 'Unknown error');
      alert('Error deleting analysis: ' + errorMsg);
    });
  }

  hasSavedAnalysis(stmt: any): boolean {
    // Check if statement has saved analysis
    if (!stmt) {
      return false;
    }
    // Check both the statement object and make sure it's not null/undefined/empty
    return !!(stmt.analysisResults && typeof stmt.analysisResults === 'object' && Object.keys(stmt.analysisResults).length > 0);
  }

  removeAnalysis(stmt) {
    if (!stmt || !stmt.id) {
      return;
    }
    
    if (!confirm('This will remove all expenses, invoices, and payments created from this statement analysis. The statement file will remain. Continue?')) {
      return;
    }
    
    const propertyId = this.property ? this.property.id : (stmt.property ? stmt.property.id : null);
    if (!propertyId) {
      alert('Unable to determine property for this statement');
      return;
    }
    
    this.loading = true;
    this.propertyService.removeAnalysis(propertyId, stmt).then((result: any) => {
      this.loading = false;
      
      // Verify statement still exists in response
      if (result && result.statementStillExists && result.statementId === stmt.id) {
        console.log(`[PropertyStatementsComponent] Statement ${stmt.id} confirmed to still exist after removal`);
      } else {
        console.warn(`[PropertyStatementsComponent] Warning: Statement existence not confirmed in response`, result);
      }
      
      alert(`Analysis removed successfully! Removed ${result.expensesRemoved || 0} expenses, ${result.invoicesRemoved || 0} invoices, and ${result.paymentsRemoved || 0} payments.`);
      
      // Update applied status
      this.statementAppliedStatus[stmt.id] = false;
      
      // Clear analysis results if this was the analyzed statement
      if (this.selectedStatementForAnalysis && this.selectedStatementForAnalysis.id === stmt.id) {
        this.analysisResults = null;
        this.selectedStatementForAnalysis = null;
      }
      
      // Reload statements directly - don't use getPublicProperty which might cache
      // Force a fresh reload by clearing cache and reloading
      this.propertyService.clearProperties();
      if (this.property) {
        // Reload property to get fresh statements, then reload statements list
        this.propertyService.getPublicProperty(this.property.id).then(property => {
          this.property = property;
          // Also explicitly reload statements to ensure we get the latest
          this.loadStatements();
        }).catch(() => {
          // If getPublicProperty fails, just reload statements directly
          this.loadStatements();
        });
      } else {
        this.loadAllStatements();
      }
    }).catch((error) => {
      this.loading = false;
      console.error('Error removing analysis:', error);
      const errorMsg = (error && error.error && error.error.error) ? error.error.error : (error.message || 'Unknown error');
      alert('Error removing analysis: ' + errorMsg);
    });
  }

  deleteStatement(stmt) {
    if (!stmt) {
      return;
    }
    const applied = this.isStatementApplied(stmt);
    const message = applied
      ? 'This statement has been applied. Deleting it also removes the expenses, invoices and payments it created, and the uploaded file. Continue?'
      : 'Delete this statement and its uploaded file? Nothing has been applied from it.';
    if (!confirm(message)) {
      return;
    }
    const propertyId = this.property ? this.property.id : (stmt.property ? stmt.property.id : null);
    if (!propertyId) {
      return;
    }
    this.loading = true;
    this.propertyService.deleteStatement(propertyId, stmt).then(() => {
      delete this.statementAppliedStatus[stmt.id];
      if (this.selectedStatementForAnalysis && this.selectedStatementForAnalysis.id === stmt.id) {
        this.closeAnalysis();
      }
      this.reloadAfterChange();
    }).catch((error) => {
      this.loading = false;
      alert('Error deleting statement: ' + apiError(error).message);
    });
  }

  applyFilter(event: Event) {
    this.searchText = (event.target as HTMLInputElement).value;
    if (this.property) {
      this.applyFilters();
    } else {
      this.applyAllFilters();
    }
  }

  applyAllFilters() {
    // For all statements view, apply client-side search/date filters on current page data only
    // Note: Property filter is handled server-side via loadAllStatements()
    let filtered = [...this.allStatements];

    // Search filter (client-side on current page)
    if (this.searchText && this.searchText.trim()) {
      const searchLower = this.searchText.toLowerCase().trim();
      filtered = filtered.filter(st => {
        const dateStr = st.date ? new Date(st.date).toLocaleDateString().toLowerCase() : '';
        const propertyName = st.property ? (st.property.addressLine1 || '').toLowerCase() : '';
        return dateStr.includes(searchLower) || propertyName.includes(searchLower);
      });
    }

    // Date filter (client-side on current page)
    if (this.selectedDateFilter !== 'all') {
      const now = new Date();
      let startDate: Date | null = null;
      let endDate: Date | null = null;

      if (this.selectedDateFilter === 'thisMonth') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      } else if (this.selectedDateFilter === 'lastMonth') {
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      } else if (this.selectedDateFilter === 'custom' && this.customStartDate && this.customEndDate) {
        startDate = new Date(this.customStartDate);
        endDate = new Date(this.customEndDate);
        endDate.setHours(23, 59, 59);
      }

      if (startDate && endDate) {
        filtered = filtered.filter(st => {
          if (!st.date) return false;
          const stDate = new Date(st.date);
          return stDate >= startDate && stDate <= endDate;
        });
      }
    }

    // For client-side filters, update filtered list but keep totalItems from server
    this.filteredStatements = filtered;
    // Don't change totalItems - it should reflect the server-side total
    // Only update current page if filters changed
    if (this.searchText || this.selectedDateFilter !== 'all') {
      this.currentPage = 0;
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
    }
    this.updateDataSource();
  }

  applyFilters() {
    // For property-specific view
    let filtered = [...this.allStatements];

    // Search filter
    if (this.searchText && this.searchText.trim()) {
      const searchLower = this.searchText.toLowerCase().trim();
      filtered = filtered.filter(st => {
        const dateStr = st.date ? new Date(st.date).toLocaleDateString().toLowerCase() : '';
        return dateStr.includes(searchLower);
      });
    }

    this.filteredStatements = filtered;
    this.totalItems = filtered.length;
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.updateDataSource();
  }

  onPropertyFilterChange(propertyId: string | null) {
    this.selectedPropertyId = propertyId;
    this.currentPage = 0;
    // Property filter is server-side, so reload from API
    this.loadAllStatements();
  }

  onDateFilterChange(filter: string) {
    this.selectedDateFilter = filter;
    if (filter !== 'custom') {
      this.customStartDate = '';
      this.customEndDate = '';
    }
    // Date filter is client-side on current page, so just apply filters
    this.applyAllFilters();
  }

  onCustomDateChange() {
    // Date filter is client-side on current page, so just apply filters
    this.applyAllFilters();
  }

  clearFilters() {
    this.selectedPropertyId = null;
    this.searchText = '';
    this.selectedDateFilter = 'all';
    this.customStartDate = '';
    this.customEndDate = '';
    if (this.property) {
      this.applyFilters();
    } else {
      this.currentPage = 0;
      this.loadAllStatements();
    }
  }

  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    if (this.property) {
      this.updateDataSource();
    } else {
      this.loadAllStatements();
    }
  }

  onRowClick(event: MouseEvent, statement: any) {
    // Prevent row click from triggering when clicking on action buttons
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) {
      return;
    }
    // View statement when row is clicked
    this.viewStatement(statement);
  }

  navigateToDashboard() {
    this.router.navigate(['/admin-landing']);
  }

  ngAfterViewInit() {
    if (this.sort && this.dataSource) {
      this.dataSource.sort = this.sort;
    }
    // Explicitly ensure paginator is NOT connected to dataSource for server-side pagination
    if (this.dataSource.paginator) {
      this.dataSource.paginator = null;
    }
  }

  private updateDataSource() {
    // For all statements view (server-side pagination), use filteredStatements directly
    // For property-specific view (client-side pagination), slice the filtered list
    if (this.property) {
      // Property-specific: client-side pagination
      const startIndex = this.currentPage * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      const paginatedData = this.filteredStatements.slice(startIndex, endIndex);
      this.dataSource.data = paginatedData;
    } else {
      // All statements: server-side pagination, data is already paginated
      this.dataSource.data = this.filteredStatements;
    }
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }
}
